import { CommonResponse } from '@gtpl/shared-models/masters';
import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository, getManager } from 'typeorm';
import { MachinesRequisitionEntity } from './entity/machines-requisition-entity';
import { machinesRequisitionIdReq } from './dto/machines-req-data-req.dto';
import { MachinesRequisitionSnapEntity } from './entity/machine-requisition-snap-entity';

@Injectable()
export class MachinesRequisitionService {
    constructor(
        @InjectRepository(MachinesRequisitionEntity)
        private machinesReqRepository: Repository<MachinesRequisitionEntity>,
        @InjectRepository(MachinesRequisitionSnapEntity)
        private machinesReqSnapRepository: Repository<MachinesRequisitionSnapEntity>,

    ) { }

    async create(data: any): Promise<CommonResponse> {
        try {
            const machinesRequisitions = data.map((item: any) => {
                // Check if any date fields are null
                if (
                    item.DATE === null ||
                    item.Date === null ||
                    item['Date Of Machines Required'] === null ||
                    item['Received On'] === null
                ) {
                    throw new Error('The following fields (DATE, Date Of Machines Required, Date, Received On) can not be null');
                }
                const machinesReq = new MachinesRequisitionEntity();
                machinesReq.sepl = item.SEPL;
                machinesReq.unit = item.UNIT;
                machinesReq.mntcfm = item.MNTC_FM;
                machinesReq.rev = item.REV;
                machinesReq.date = item.DATE;
                machinesReq.to = item.To;
                machinesReq.unit_1 = item.Unit;
                machinesReq.style = item.Style;
                machinesReq.dateOne = item.Date;
                machinesReq.poNOs = item['P.O.Nos'];
                machinesReq.buyer = item.Buyer;
                machinesReq.orderQty = item.OrderQty;
                machinesReq.machineDesc = item['Machine Description'];
                machinesReq.noOfBatches = item['No of Batches'];
                machinesReq.noOfMachinesReqPerBatch = item['No of Machines Required Per Batch'];
                machinesReq.totalMachinesRequired = item['Total Machines Required'];
                machinesReq.atPresentHoldingMachinesParticularStyle = item['At Present Holding Machines Particular Style'];
                machinesReq.extraMachinesRequiredParticularStyle = item['Extra Machines Required Particular Style'];
                machinesReq.totalMachinesExistingInTheUnit = item['Total Machines Existing in the Unit'];
                machinesReq.dateOfMachinesRequired = item['Date Of Machines Required'];
                machinesReq.noOfDaysMachinesRequired = item['No Of Days Machines Required'];
                machinesReq.preparedBy = item['Prepared By'];
                machinesReq.approvedBy = item['Approved By'];
                machinesReq.authoredBy = item['Authored By'];
                machinesReq.note = item.Note;
                machinesReq.receivedBy = item['Received By'];
                machinesReq.receivedOn = item['Received On'];

                return machinesReq;
            });

            await this.machinesReqRepository.save(machinesRequisitions);

            return new CommonResponse(true, 565, 'Created successfully');
        } catch (error) {
            console.error(error.message || error);
            return new CommonResponse(false, 554, 'The following fields (DATE, Date Of Machines Required, Date, Received On) can not be null');
        }
    }

    async getAllMechinesRequisitionData(): Promise<CommonResponse> {
        const manager = getManager();
        let data
        data=`SELECT * FROM shahi_assets.machines_requisition
        GROUP BY shahi_assets.machines_requisition.PO_Nos`
        const response = await manager.query(data)
        if (response) {
            return new CommonResponse(true, 11, 'Data retrieved successfully', response)
        } else {
            return new CommonResponse(false, 22, 'No data found')
        }
    }

    async getMechinesRequisitionDataByPONOs(req: machinesRequisitionIdReq): Promise<CommonResponse> {
        const response = await this.machinesReqRepository.find({ where: { poNOs: req.poNOs } });
        if (response) {
            return new CommonResponse(true, 1, 'Data retrieved successfully', response)
        } else {
            return new CommonResponse(false, 99898, 'No data found')
        }
    }

    async createSnap(snapData: any): Promise<CommonResponse> {
        try {
            const savedSnapData = Object.values(snapData[0])
                .filter(data => typeof data === 'object') // Ensure we're only processing objects
                .map((data: any) => {
                    const machinesReq = new MachinesRequisitionSnapEntity();
                    machinesReq.machineReqId = data.machinesReqId;
                    machinesReq.sepl = data.sepl;
                    machinesReq.unit = data.unit;
                    machinesReq.mntcfm = data.mntcfm;
                    machinesReq.rev = data.rev;
                    machinesReq.date = new Date(data.date);
                    machinesReq.to = data.to;
                    machinesReq.unit_1 = data.unit_1;
                    machinesReq.style = data.style;
                    machinesReq.dateOne = new Date(data.dateOne);
                    machinesReq.poNOs = data.poNOs;
                    machinesReq.buyer = data.buyer;
                    machinesReq.orderQty = data.orderQty;
                    machinesReq.machineDesc = data.machineDesc;
                    machinesReq.noOfBatches = data.noOfBatches;
                    machinesReq.noOfMachinesReqPerBatch = data.noOfMachinesReqPerBatch;
                    machinesReq.totalMachinesRequired = data.totalMachinesRequired;
                    machinesReq.atPresentHoldingMachinesParticularStyle = data.atPresentHoldingMachinesParticularStyle;
                    machinesReq.extraMachinesRequiredParticularStyle = data.extraMachinesRequiredParticularStyle;
                    machinesReq.totalMachinesExistingInTheUnit = data.totalMachinesExistingInTheUnit;
                    machinesReq.dateOfMachinesRequired = new Date(data.dateOfMachinesRequired);
                    machinesReq.noOfDaysMachinesRequired = data.noOfDaysMachinesRequired;
                    machinesReq.preparedBy = data.preparedBy;
                    machinesReq.approvedBy = data.approvedBy;
                    machinesReq.authoredBy = data.authoredBy;
                    machinesReq.note = data.note;
                    machinesReq.status = snapData[0].status; // Using the status from the top-level object
                    machinesReq.receivedBy = data.receivedBy;
                    machinesReq.receivedOn = new Date(data.receivedOn);
                    machinesReq.isActive = data.isActive;
                    machinesReq.createdAt = new Date(data.createdAt);
                    machinesReq.createdUser = data.createdUser;
                    machinesReq.updatedAt = new Date(data.updatedAt);
                    machinesReq.updatedUser = data.updatedUser;
                    machinesReq.versionFlag = data.versionFlag;
                    return machinesReq;
                });
    
            await this.machinesReqSnapRepository.save(savedSnapData);
    
            return new CommonResponse(true, 52, 'Created successfully');
        } catch (error) {
            console.error(error.message || error);
            return new CommonResponse(false, 54, 'The following fields (DATE, Date Of Machines Required, Date, Received On) can not be null');
        }
    }
    

    async getAllMechinesRequisitionSnapData(): Promise<CommonResponse> {
        const manager = getManager();
        let data
        data=`SELECT * FROM shahi_assets.machines_requisition_snap
        GROUP BY shahi_assets.machines_requisition_snap.PO_Nos`
        const response = await manager.query(data)
        if (response) {
            return new CommonResponse(true, 11, 'Data retrieved successfully', response)
        } else {
            return new CommonResponse(false, 22, 'No data found')
        }
    }

    async getMechinesRequisitionSnapDataByPoNos(req: machinesRequisitionIdReq): Promise<CommonResponse> {
        const response = await this.machinesReqSnapRepository.find({ where: { poNOs: req.poNOs } });
        if (response) {
            return new CommonResponse(true, 1, 'Data retrieved successfully', response)
        } else {
            return new CommonResponse(false, 998, 'No data found')
        }
    }
    
}
